import { useEffect, useRef, useCallback } from 'react';
import { Box } from '@mui/material';

const Confetti = ({
  count = 50,
  colors = ['#ff6b6b', '#ffd93d', '#6bcb77', '#4d96ff', '#ff6fff'],
  duration = 3000,
  spread = 360,
  gravity = 0.5,
  onComplete,
  trigger = true
}) => {
  const canvasRef = useRef(null);
  const animationRef = useRef(null);
  const particlesRef = useRef([]);

  const createParticle = useCallback((canvas) => {
    const centerX = canvas.width / 2;
    const centerY = canvas.height / 2;
    const angle = (Math.random() * spread * Math.PI) / 180;
    const velocity = 8 + Math.random() * 8;

    return {
      x: centerX,
      y: centerY,
      vx: Math.cos(angle) * velocity * (Math.random() > 0.5 ? 1 : -1),
      vy: -Math.abs(Math.sin(angle) * velocity) - Math.random() * 4,
      color: colors[Math.floor(Math.random() * colors.length)],
      size: 6 + Math.random() * 6,
      rotation: Math.random() * 360,
      rotationSpeed: (Math.random() - 0.5) * 10,
      shape: Math.random() > 0.5 ? 'rect' : 'circle',
      opacity: 1,
    };
  }, [colors, spread]);

  const animate = useCallback((ctx, canvas, startTime) => {
    const elapsed = Date.now() - startTime;
    const progress = Math.min(elapsed / duration, 1);

    ctx.clearRect(0, 0, canvas.width, canvas.height);

    particlesRef.current.forEach((p) => {
      p.x += p.vx;
      p.y += p.vy;
      p.vy += gravity;
      p.rotation += p.rotationSpeed;
      p.opacity = 1 - progress;

      ctx.save();
      ctx.translate(p.x, p.y);
      ctx.rotate((p.rotation * Math.PI) / 180);
      ctx.globalAlpha = p.opacity;
      ctx.fillStyle = p.color;

      if (p.shape === 'rect') {
        ctx.fillRect(-p.size / 2, -p.size / 4, p.size, p.size / 2);
      } else {
        ctx.beginPath();
        ctx.arc(0, 0, p.size / 2, 0, Math.PI * 2);
        ctx.fill();
      }

      ctx.restore();
    });

    if (progress < 1) {
      animationRef.current = requestAnimationFrame(() =>
        animate(ctx, canvas, startTime)
      );
    } else {
      onComplete?.();
    }
  }, [duration, gravity, onComplete]);

  useEffect(() => {
    if (!trigger) return;

    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    canvas.width = canvas.offsetWidth;
    canvas.height = canvas.offsetHeight;

    particlesRef.current = Array.from({ length: count }, () =>
      createParticle(canvas)
    );

    const startTime = Date.now();
    animate(ctx, canvas, startTime);

    return () => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
    };
  }, [trigger, count, createParticle, animate]);

  if (!trigger) return null;

  return (
    <Box
      component="canvas"
      ref={canvasRef}
      sx={{
        position: 'absolute',
        inset: 0,
        pointerEvents: 'none',
        zIndex: 1000,
      }}
    />
  );
};

export default Confetti;
